import { FOOTER_PREMIUM } from "../common/footer";
import { ONBOARDING_HEADER } from "../common/header";

/**
 * World-Class Onboarding Day 3 Template
 * Features: AI features showcase, progress tracking, step-by-step guide, pro tips
 */
export const onboardingDay3 = `
<mjml>
  <mj-head>
    <mj-title>Supercharge Your Bot with AI - Day 3</mj-title>
    <mj-preview>Unlock the power of AI to make your chatbot smarter</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .ai-badge { background: linear-gradient(135deg, #8B5CF6, #7C3AED); color: white; padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 700; }
      .feature-icon { background: linear-gradient(135deg, #F3E8FF, #EDE9FE); width: 56px; height: 56px; border-radius: 14px; display: inline-block; line-height: 56px; text-align: center; font-size: 28px; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#faf5ff">
    \${ONBOARDING_HEADER("AI-Powered Conversations", "Day 3 of your journey")}

    <!-- Progress Section -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text align="center" padding-bottom="20px">
          <span style="background: linear-gradient(135deg, #8B5CF6, #7C3AED); color: white; padding: 8px 20px; border-radius: 25px; font-size: 13px; font-weight: 700;">&#129302; Email 3 of 5</span>
        </mj-text>
        <mj-text font-size="26px" color="#0f172a" font-weight="700" align="center" padding-bottom="12px">
          Make Your Chatbot Smarter with AI
        </mj-text>
        <mj-text font-size="15px" color="#64748b" align="center">
          Hey {{first_name}}, ready to take your chatbot to the next level? Today we're diving into AI capabilities that will make your bot more intelligent and helpful.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Progress Bar -->
    <mj-section background-color="#ffffff" padding="25px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="13px" color="#64748b" padding-bottom="12px">
          Your onboarding progress:
        </mj-text>
        <mj-raw>
          <div style="background-color: #e2e8f0; border-radius: 10px; height: 12px; overflow: hidden;">
            <div style="background: linear-gradient(90deg, #8B5CF6, #A855F7); height: 100%; width: 60%; border-radius: 10px;"></div>
          </div>
        </mj-raw>
        <mj-text font-size="13px" color="#8B5CF6" font-weight="600" padding-top="8px">
          60% Complete - You're doing great!
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- AI Features Section -->
    <mj-section background-color="#ffffff" padding="30px 30px 15px" border-radius="16px 16px 0 0">
      <mj-column>
        <mj-text font-size="12px" color="#8B5CF6" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Powerful Features
        </mj-text>
        <mj-text font-size="22px" color="#0f172a" font-weight="700">
          AI Features You Can Enable Today
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Feature 1 -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#faf5ff" border-radius="12px" border-left="4px solid #8B5CF6">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 70px; vertical-align: top;">
                <span style="background: linear-gradient(135deg, #F3E8FF, #EDE9FE); width: 50px; height: 50px; border-radius: 12px; display: inline-block; line-height: 50px; text-align: center; font-size: 24px;">&#129504;</span>
              </td>
              <td style="vertical-align: top;">
                <strong style="font-size: 17px; color: #7C3AED;">Natural Language Understanding</strong><br>
                <span style="color: #64748b; font-size: 14px;">Your bot understands user intent even when they don't use exact keywords. "I need help with my order" and "Where's my package?" both trigger order support.</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Feature 2 -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#faf5ff" border-radius="12px" border-left="4px solid #8B5CF6">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 70px; vertical-align: top;">
                <span style="background: linear-gradient(135deg, #F3E8FF, #EDE9FE); width: 50px; height: 50px; border-radius: 12px; display: inline-block; line-height: 50px; text-align: center; font-size: 24px;">&#128161;</span>
              </td>
              <td style="vertical-align: top;">
                <strong style="font-size: 17px; color: #7C3AED;">Smart Responses</strong><br>
                <span style="color: #64748b; font-size: 14px;">Enable AI-generated responses that adapt to context. Your bot learns from your knowledge base and provides accurate, helpful answers.</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Feature 3 -->
    <mj-section background-color="#ffffff" padding="10px 30px 30px" border-radius="0 0 16px 16px">
      <mj-column padding="20px" background-color="#faf5ff" border-radius="12px" border-left="4px solid #8B5CF6">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 70px; vertical-align: top;">
                <span style="background: linear-gradient(135deg, #F3E8FF, #EDE9FE); width: 50px; height: 50px; border-radius: 12px; display: inline-block; line-height: 50px; text-align: center; font-size: 24px;">&#128150;</span>
              </td>
              <td style="vertical-align: top;">
                <strong style="font-size: 17px; color: #7C3AED;">Sentiment Analysis</strong><br>
                <span style="color: #64748b; font-size: 14px;">Detect frustrated users automatically and escalate to human support. Keep customers happy by responding to emotional cues.</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- How to Enable Section -->
    <mj-section background-color="#f0f9ff" padding="30px" border-radius="16px" border="2px solid #bae6fd">
      <mj-column>
        <mj-text font-size="18px" color="#0f172a" font-weight="700" padding-bottom="15px">
          &#128736; How to Enable AI Features
        </mj-text>
        <mj-text font-size="14px" color="#64748b" line-height="2">
          <strong style="color: #0f172a;">Step 1:</strong> Go to your chatbot settings<br/>
          <strong style="color: #0f172a;">Step 2:</strong> Click on "AI Configuration"<br/>
          <strong style="color: #0f172a;">Step 3:</strong> Toggle on the AI features you want<br/>
          <strong style="color: #0f172a;">Step 4:</strong> Upload your knowledge base (FAQs, docs, etc.)
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Pro Tip Section -->
    <mj-section background-color="#FEF3C7" padding="25px 30px" border-radius="16px" border-left="4px solid #F59E0B">
      <mj-column>
        <mj-text font-size="16px" color="#92400E" font-weight="700" padding-bottom="8px">
          &#128161; Pro Tip
        </mj-text>
        <mj-text font-size="14px" color="#92400E">
          Start with a small knowledge base and expand over time. Upload your most frequently asked questions first, then add more content as you see what users are asking.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- CTA Section -->
    <mj-section background-color="#8B5CF6" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="24px" color="#ffffff" font-weight="700" align="center" padding-bottom="10px">
          Ready to Add AI to Your Bot?
        </mj-text>
        <mj-text font-size="15px" color="rgba(255,255,255,0.9)" align="center" padding-bottom="20px">
          Enable AI features and watch your chatbot get smarter.
        </mj-text>
        <mj-button background-color="#ffffff" color="#8B5CF6" href="{{ai_settings_url}}" border-radius="8px" padding="18px 45px" font-size="17px" font-weight="700">
          Enable AI Features &#8594;
        </mj-button>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- What's Coming Section -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="18px" color="#0f172a" font-weight="700" padding-bottom="10px">
          Coming Up Next
        </mj-text>
        <mj-text font-size="14px" color="#64748b">
          <strong style="color: #0f172a;">Tomorrow (Day 4):</strong> Integrating with your favorite tools - CRM, email marketing, and more!
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "onboardingDay3",
  name: "Onboarding - Day 3 AI Features",
  category: "sequences",
  purpose: "sequence" as const,
  description:
    "A world-class Day 3 onboarding email showcasing AI features with icon cards, step-by-step enablement guide, pro tips, and gradient progress tracking.",
  features: [
    "AI badge with email count",
    "Visual progress bar",
    "Feature cards with icons",
    "Step-by-step enablement guide",
    "Pro tip highlight",
    "Preview of next email",
  ],
  subject: "&#129302; Supercharge Your Bot with AI - Day 3",
  subTitle: "Unlock the power of AI",
  mjmlTemplate: onboardingDay3,
  tags: ["sequence", "onboarding", "ai", "day3", "features"],
};
